import React, { useMemo } from 'react';
// Removed ReactDOM import as portal is being removed
import DOMPurify from 'dompurify';

interface CitationCardProps {
  source: any;
  // Position will now be controlled more by CSS relative to parent,
  // but JS might still pass hints or offsets if needed.
  // For now, let's assume CSS handles most of it, and JS for dynamic flip/clamping.
  position?: { top?: string | number; bottom?: string | number; left?: string | number; right?: string | number; transform?: string };
  visible: boolean;
  cleanText?: (text: string, index?: number) => string;
  onRequestClose: () => void;
  // Add a prop to indicate if it should render above the marker
  renderAbove?: boolean;
  onMouseEnterCard?: () => void;
  onMouseLeaveCard?: () => void;
}

/**
 * A professional, Grok-style floating card that presents citation information.
 * It intelligently positions itself near the citation marker on larger screens and
 * collapses into a mobile-friendly bottom sheet on small devices.
 */
const CitationCard: React.FC<CitationCardProps> = ({ source, position, visible, onRequestClose, renderAbove, onMouseEnterCard, onMouseLeaveCard }) => {
  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;

  // Styles will now be simpler, relying on parent relative positioning
  const cardStyle: React.CSSProperties = useMemo(() => {
    if (isMobile) { // Mobile can still use fixed/bottom sheet style
      return {
        position: 'fixed',
        left: '50%',
        bottom: '24px',
        transform: 'translateX(-50%)',
        zIndex: 2147483647,
        maxWidth: '92%',
        width: '92%',
      };
    }
    // Default desktop style, position: absolute is set by className or here
    // CSS will handle the actual top/bottom/left/right relative to parent
    // The `position` prop can provide overrides or dynamic adjustments from JS if needed.
    return {
      position: 'fixed',
      zIndex: 99999, // Very high z-index to ensure visibility
      maxWidth: '420px',
      width: '420px',
      ...(position || {}), // Allow JS to override/set position values
    } as React.CSSProperties;
  }, [isMobile, position]);

  const getFavicon = (url?: string): string | undefined => {
    if (!url) return undefined;
    try {
      const { hostname } = new URL(url);
      return `https://www.google.com/s2/favicons?domain=${hostname}`;
    } catch {
      return undefined;
    }
  };

  const previewHtml: string = useMemo(() => {
    if (!source) return '';
    let rawText = source.cited_text || source.sentence || source.text || '';
    
    // Simply strip out markdown symbols
    rawText = rawText
      // Remove #, ##, ### etc. at start of lines or after spaces
      .replace(/^[#]+\s*/gm, '')
      .replace(/\s+[#]+\s*/g, ' ')
      // Remove standalone ** or * 
      .replace(/\s*[*]+\s*/g, ' ')
      // Remove any backslashes
      .replace(/\\/g, '')
      // Clean up extra spaces and newlines
      .replace(/\s+/g, ' ')
      .trim();
    
    try {
      // No need for markdown conversion anymore since we stripped the symbols
      return DOMPurify.sanitize(rawText);
    } catch (error) {
      console.error("Error processing citation card preview text:", error);
      return rawText;
    }
  }, [source]);

  // Removed internal handleMouseLeave, parent now controls hide via onMouseLeaveCard prop
  // const handleMouseLeave = () => {
  //   if (onRequestClose) {
  //     onRequestClose();
  //   }
  // };
  
  // The card itself, no portal
  if (!visible || !source) {
    return null;
  }

  return (
    <div
      className={`citation-card citation-tooltip bg-gray-800 text-gray-200 shadow-2xl rounded-lg border border-gray-700 p-4`}
      style={cardStyle}
      onMouseEnter={onMouseEnterCard}
      onMouseLeave={onMouseLeaveCard}
    >
      {/* Header */}
      <div className="flex items-center mb-3 border-b border-gray-700 pb-2">
        {getFavicon(source?.url) && (
          <img src={getFavicon(source.url)} alt="favicon" className="w-5 h-5 mr-2 rounded-sm" />
        )}
        <div className="flex-1 min-w-0">
          {source?.url ? (
            <a 
              href={source.url} 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-sm font-semibold truncate mb-0.5 text-gray-100 hover:text-brand-400 hover:underline block"
              title={source?.title || 'Untitled Source'}
            >
              {source?.title || 'Untitled Source'}
            </a>
          ) : (
            <h4 className="text-sm font-semibold truncate mb-0.5 text-gray-100" title={source?.title || 'Untitled Source'}>
              {source?.title || 'Untitled Source'}
            </h4>
          )}
        </div>
      </div>

      {/* Preview (scrollable) */}
      {previewHtml && (
        <div 
          className="preview-html max-h-24 overflow-y-auto pr-1 text-sm leading-relaxed text-white prose prose-sm prose-citation-card prose-headings:text-gray-100 prose-p:text-gray-200 prose-strong:text-gray-100 prose-em:text-gray-100 prose-a:text-brand-400 hover:prose-a:text-brand-300" 
          style={{ wordBreak: 'break-word' }}
          dangerouslySetInnerHTML={{ __html: previewHtml }}
        />
      )}
    </div>
  );
};

export default CitationCard; 